import { MenuItem, Select } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isNull, shuffle } from 'lodash';

import Constants from '../../../constants';
import { FillBlankComponent } from 'content/models/LessonComponent';
import LessonResponseService from '../../../services/LessonResponseService';
import styles from '../../../styles/FillBlank.module.css';
import { useLessonComponentContext } from '../../../models/LessonComponentContext';
import { useLessonContext } from '../../../models/LessonContext';
import useThemes from '../../../../shared/hooks/useThemes';

interface IFillGapProps {
  id: string;
  value: string;
  component: FillBlankComponent;
  onChange: (id: string, value: string) => void;
}

/** Renders the text input field for all blank elements */
export const FillGap = (props: IFillGapProps) => {
  const { id, value, component, onChange } = props;
  const { correct, showFeedback, answerSaved, isSurvey } =
    useLessonComponentContext();
  const { mode } = useLessonContext();
  const withMenus = component.type === 'fill_menu';
  const [style, setStyle] = useState('');
  const { muiTheme } = useThemes();
  const fontFamily = muiTheme.typography.fontFamily;

  // Fill gap.
  const metric = useRef<HTMLSpanElement>(null);
  const input = useRef<HTMLInputElement>(null);
  // Fill menu.
  const blank = component.content.blank.find((b) => b.id === id);
  const options = useMemo(
    () => shuffle(blank.options),
    [id, JSON.stringify(blank.options)]
  );

  useEffect(() => {
    if (metric.current && input.current) {
      // Use a separate <span> element to measure the width of the current answer.
      const width = metric.current.getBoundingClientRect().width;
      input.current.style.width = `${width || 48}px`;
    }
  }, [value]);

  useEffect(() => {
    setStyle('');
    if ((!isNull(correct) && showFeedback) || mode === 'mark') {
      if (value && !isSurvey && answerSaved) {
        setStyle(
          LessonResponseService.isEachBlankAnswerCorrect(
            component.content.blank,
            id,
            value,
            component.content.correctResponsePattern
          )
            ? styles.blankCorrect
            : styles.blankIncorrect
        );
      }
    }
  }, [id, value, correct, showFeedback, mode, isSurvey, answerSaved]);

  return withMenus ? (
    <Select
      value={value || '~'}
      variant="standard"
      title={Constants.FillMenuPlaceholder}
      onChange={(e) => props.onChange(id, e.target.value as string)}
      classes={{
        select: `${value && answerSaved ? styles.blankHideArrow : ''} ${
          styles.blankSelect
        } ${style}`,
        icon: `${
          value && answerSaved
            ? styles.selectSvgIconHide
            : styles.selectSvgIconDisplay
        }`
      }}
      style={{
        font: 'inherit',
        color: !value ? 'grey' : 'inherit'
      }}
      disableUnderline
    >
      <MenuItem value="~" disabled>
        {Constants.FillMenuPlaceholder}
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={option} style={{ fontFamily }}>
          {option}
        </MenuItem>
      ))}
    </Select>
  ) : (
    <span>
      <span
        ref={metric}
        style={{
          whiteSpace: 'nowrap',
          position: 'absolute',
          opacity: 0,
          zIndex: -1
        }}
      >
        {value?.replace(/ /g, '\u00a0')}
      </span>
      <input
        ref={input}
        value={value || ''}
        maxLength={25}
        onChange={(e) => onChange(id, e.target.value)}
        className={`${styles.blankInput} ${style}`}
        style={{
          fontSize: 'inherit',
          fontFamily: 'inherit',
          fontWeight: 'inherit',
          textAlign: value ? 'center' : undefined,
          //Deduct border-bottom value of `blankInput`. Space between the blank lines matches
          //when line-height is 1.6. (`ql-editor-fill-the-blank`)
          margin: '-1px 0px -2px'
        }}
      />
    </span>
  );
};
